// The assistant, shared by the brand portal and the admin. The mascot waits
// at the bottom right; a click (or Ctrl/⌘ J) opens the panel on the right,
// docked beside the page on a wide screen so both stay in view. The person
// asks in their own words. What the assistant looks up shows as a short list
// of steps; a change shows as a card that says who and what in plain words,
// and runs only on its button. The mascot in the header shows where things
// stand: waving, working, winking after a change, sorry after a failure.
//
// The conversation stays in this tab (sessionStorage) and goes to the Worker
// whole each time: the Worker keeps nothing.

import {Fragment, useCallback, useEffect, useRef, useState} from 'react';
import type {FormEvent, KeyboardEvent, ReactNode} from 'react';
import type {AssistantEvent, AssistantLink, AssistantMessages, AssistantPendingCall, AssistantRequest} from '../../../shared/assistant-api';
import './assistant.css';

type StepStatus = 'running' | 'done' | 'error' | 'declined';

interface Step {
  id: string;
  label: string;
  status: StepStatus;
  link?: AssistantLink;
}

type Entry =
  | {kind: 'user'; text: string}
  | {kind: 'assistant'; text: string}
  | {kind: 'steps'; steps: Step[]}
  | {kind: 'confirm'; calls: AssistantPendingCall[]; state: 'pending' | 'approved' | 'declined' | 'mixed'}
  | {kind: 'notice'; text: string}
  | {kind: 'error'; text: string};

interface Saved {
  entries: Entry[];
  messages: AssistantMessages;
}

const KINDS = new Set<Entry['kind']>(['user', 'assistant', 'steps', 'confirm', 'notice', 'error']);

type Mood = 'wave' | 'wait' | 'wink' | 'oops';

const MASCOT: Record<Mood, string> = {
  wave: '/img/mascot-wave.webp',
  wait: '/img/mascot-wait.webp',
  wink: '/img/mascot-wink.webp',
  oops: '/img/mascot-oops.webp',
};

const ERRORS: Record<string, string> = {
  assistant_unavailable: 'I can’t think straight right now. Try again in a moment.',
  busy: 'I’m a bit busy. Try again in a minute.',
  no_credit: 'I’m out of credit for now. The team is on it.',
  rate_limited: 'That’s a lot of questions for one hour. Let’s pick it up a little later.',
  conversation_too_long: 'This chat got long. Start a new one and I’ll keep up.',
  unauthorized: 'Your session has ended. Sign in again.',
  agreement_required: 'Sign the agreement first: I act for your company.',
  brand_paused: 'Your workspace is paused. Contact the Earn team.',
  nothing_to_confirm: 'That change isn’t waiting anymore. Ask me again.',
};

function describeError(code: string): string {
  return ERRORS[code] ?? 'Something went wrong on my side. Try again.';
}

function load(key: string): Saved {
  try {
    const raw = window.sessionStorage.getItem(key);
    const saved = raw ? JSON.parse(raw) as Saved : null;
    // A chat saved by an older panel may hold kinds this one no longer draws.
    if (saved && Array.isArray(saved.entries) && Array.isArray(saved.messages)) {
      return {...saved, entries: saved.entries.filter((entry) => KINDS.has(entry?.kind))};
    }
  } catch {
    // Storage blocked, or a chat from an older panel: start fresh.
  }
  return {entries: [], messages: []};
}

function save(key: string, value: Saved): void {
  try { window.sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* it still works; it just won't survive a reload */ }
}

function greetedThisVisit(key: string): boolean {
  try { return window.sessionStorage.getItem(`${key}:greeted`) === '1'; } catch { return false; }
}

function markGreeted(key: string): void {
  try { window.sessionStorage.setItem(`${key}:greeted`, '1'); } catch { /* it greets again next time */ }
}

function remembered(key: string): boolean {
  try { return window.localStorage.getItem(key) === '1'; } catch { return false; }
}

function remember(key: string, value: boolean): void {
  try { window.localStorage.setItem(key, value ? '1' : '0'); } catch { /* for this visit only */ }
}

/* ----------------------------------------------------------------- text */

/** **bold**, `code` and [links](https://… or /path), as elements: the model's text is never parsed as HTML. */
function inline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const pattern = /\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(((?:https?:\/\/|\/)[^)\s]+)\)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text))) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1]) parts.push(<strong key={parts.length}>{match[1]}</strong>);
    else if (match[2]) parts.push(<code key={parts.length}>{match[2]}</code>);
    else parts.push(<a key={parts.length} href={match[4]} {...(match[4]!.startsWith('/') ? {} : {target: '_blank', rel: 'noopener noreferrer'})}>{match[3]}</a>);
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

/** Paragraphs, lists and small headings: what a short chat answer needs. */
function Rich({text}: {text: string}) {
  const blocks = text.trim().split(/\n{2,}/);
  return <>{blocks.map((block, index) => {
    const lines = block.split('\n');
    if (lines.every((line) => /^\s*([-*•]|\d+\.)\s+/.test(line))) {
      const ordered = /^\s*\d+\./.test(lines[0]!);
      const items = lines.map((line, item) => <li key={item}>{inline(line.replace(/^\s*([-*•]|\d+\.)\s+/, ''))}</li>);
      return ordered ? <ol key={index}>{items}</ol> : <ul key={index}>{items}</ul>;
    }
    if (lines.length === 1 && /^#{1,4}\s/.test(lines[0]!)) return <h4 key={index}>{inline(lines[0]!.replace(/^#+\s*/, ''))}</h4>;
    return <p key={index}>{lines.map((line, item) => <Fragment key={item}>{item ? <br /> : null}{inline(line)}</Fragment>)}</p>;
  })}</>;
}

/* ---------------------------------------------------------------- icons */

const svg = {width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.9,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true};
const Close = () => <svg {...svg}><path d="M6 6l12 12M18 6L6 18" /></svg>;
const Fresh = () => <svg {...svg}><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z" /></svg>;
const Send = () => <svg {...svg}><path d="M12 19V5M5 12l7-7 7 7" /></svg>;
const Check = () => <svg {...svg} width={14} height={14} strokeWidth={2.4}><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>;
const Chevron = ({open}: {open: boolean}) => <svg {...svg} width={14} height={14} style={{transform: open ? 'rotate(180deg)' : 'none'}}><path d="M6 9l6 6 6-6" /></svg>;
const Arrow = () => <svg {...svg} width={14} height={14}><path d="M5 12h14M13 6l6 6-6 6" /></svg>;

/* ---------------------------------------------------------------- parts */

function Mascot({mood, size, className = ''}: {mood: Mood; size: number; className?: string}) {
  return <img className={`ebc-asst-mascot ${mood} ${className}`} src={MASCOT[mood]} width={size} height={size} alt="" draggable={false} />;
}

function StepIcon({status}: {status: StepStatus}) {
  if (status === 'done') return <span className="ebc-asst-step-icon done"><Check /></span>;
  return <span className={`ebc-asst-step-icon ${status}`} />;
}

/** What the assistant did in one go: each step while it works, a short line and the changes' links once done. */
function Steps({steps, live}: {steps: Step[]; live: boolean}) {
  const [open, setOpen] = useState(false);
  if (live || steps.some((step) => step.status === 'running')) {
    return <div className="ebc-asst-steps">{steps.map((step) => (
      <div className={`ebc-asst-step ${step.status}`} key={step.id}><StepIcon status={step.status} />{step.label}</div>
    ))}</div>;
  }
  const changes = steps.filter((step) => step.link);
  const looked = steps.filter((step) => !step.link);
  return (
    <div className="ebc-asst-steps done">
      {changes.map((step) => (
        <a className="ebc-asst-result" href={step.link!.href} key={step.id}>
          <span className="ebc-asst-step-icon done"><Check /></span><span>{step.label}</span><b>{step.link!.label}<Arrow /></b>
        </a>
      ))}
      {looked.length ? (
        <>
          <button type="button" className="ebc-asst-steps-toggle" onClick={() => setOpen((value) => !value)} aria-expanded={open} disabled={looked.length === 1}>
            {looked.length === 1 ? looked[0]!.label : `Looked at ${looked.length} things`}{looked.length > 1 ? <Chevron open={open} /> : null}
          </button>
          {open && looked.length > 1 ? looked.map((step) => (
            <div className={`ebc-asst-step ${step.status}`} key={step.id}><StepIcon status={step.status} />{step.label}</div>
          )) : null}
        </>
      ) : null}
    </div>
  );
}

function fieldLabel(key: string): string {
  const words = key.replace(/_(cents|id|json)$/, '').replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function fieldValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '–';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

/** A change waiting for the person: what happens, to what, the words that will be sent. */
function ChangeCard({entry, live, onDecide}: {entry: Extract<Entry, {kind: 'confirm'}>; live: boolean; onDecide(decisions: Record<string, 'approve' | 'decline'>): void}) {
  const [chosen, setChosen] = useState<Record<string, boolean>>(() => Object.fromEntries(entry.calls.map((call) => [call.id, true])));
  const [details, setDetails] = useState(false);
  const many = entry.calls.length > 1;
  const pending = entry.state === 'pending';
  const decide = (approve: boolean) => onDecide(Object.fromEntries(entry.calls.map((call) =>
    [call.id, approve && (chosen[call.id] ?? true) ? 'approve' : 'decline'])));
  return (
    <div className={`ebc-asst-card ${entry.state}`}>
      <span className="ebc-asst-card-tag">{pending ? 'Needs your OK' : entry.state === 'declined' ? 'Skipped' : entry.state === 'mixed' ? 'Partly done' : 'Approved'}</span>
      {entry.calls.map((call) => (
        <div className="ebc-asst-change" key={call.id}>
          <div className="ebc-asst-change-head">
            {many && pending ? <input type="checkbox" checked={chosen[call.id] ?? true} aria-label={call.preview.heading}
              onChange={(event) => setChosen((current) => ({...current, [call.id]: event.target.checked}))} /> : null}
            <b>{call.preview.heading}</b>
          </div>
          {call.preview.subject ? <span className="ebc-asst-change-subject">{call.preview.subject}</span> : null}
          {call.preview.facts.length ? <div className="ebc-asst-facts">{call.preview.facts.map((fact) => <span key={fact}>{fact}</span>)}</div> : null}
          {call.preview.quote ? <blockquote>{call.preview.quote}</blockquote> : null}
          {details ? <dl className="ebc-asst-details">{Object.entries(call.input).filter(([key]) => key !== 'dry_run').map(([key, value]) => (
            <Fragment key={key}><dt>{fieldLabel(key)}</dt><dd>{fieldValue(value)}</dd></Fragment>
          ))}</dl> : null}
        </div>
      ))}
      {pending ? (
        <div className="ebc-asst-card-actions">
          <button type="button" className="ebc-asst-approve" disabled={!live} onClick={() => decide(true)}>
            {many ? 'Approve selected' : entry.calls[0]!.preview.action}
          </button>
          <button type="button" className="ebc-asst-skip" disabled={!live} onClick={() => decide(false)}>Not now</button>
          <button type="button" className="ebc-asst-more" onClick={() => setDetails((value) => !value)}>{details ? 'Hide details' : 'Details'}</button>
        </div>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- panel */

export interface AssistantPanelProps {
  /** POST endpoint: /api/brand/assistant or /admin/api/assistant. */
  endpoint: string;
  /** Where this tab keeps the conversation; one per surface and person. */
  storageKey: string;
  /** The page open now, inside the app (for "this campaign"). */
  page: string;
  /** Who is asking, for the greeting. */
  firstName?: string;
  /** What to ask, for an empty conversation; best when it fits the page. */
  suggestions: string[];
  /** Called after an approved change ran, so the page can show it. */
  onChanged?(): void;
  /** The page has a tab bar along the bottom on a phone: the mascot sits above it. */
  aboveTabBar?: boolean;
}

/** Wide enough to keep the page in view beside the panel. */
const DOCK_QUERY = '(min-width: 1280px)';

export function AssistantPanel({endpoint, storageKey, page, firstName, suggestions, onChanged, aboveTabBar = false}: AssistantPanelProps) {
  const [open, setOpen] = useState(() => remembered(`${storageKey}:open`));
  const [met, setMet] = useState(() => remembered(`${storageKey}:met`));
  // The hello shows once a visit, for a few seconds, then the mascot alone
  // stays: a label that stayed covered the page's own buttons.
  const [greeting, setGreeting] = useState(() => !met && !greetedThisVisit(storageKey));
  useEffect(() => {
    if (!greeting) return undefined;
    markGreeted(storageKey);
    const timer = window.setTimeout(() => setGreeting(false), 6000);
    return () => window.clearTimeout(timer);
  }, [greeting, storageKey]);
  const [chat, setChat] = useState<Saved>(() => load(storageKey));
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastRequest, setLastRequest] = useState<{request: AssistantRequest; entries: Entry[]} | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLTextAreaElement>(null);
  const panel = useRef<HTMLElement>(null);
  const pending = chat.entries.some((entry) => entry.kind === 'confirm' && entry.state === 'pending');

  const last = chat.entries.at(-1);
  const recentChange = chat.entries.slice(-3).some((entry) => entry.kind === 'steps' && entry.steps.some((step) => step.link));
  const mood: Mood = busy ? 'wait' : last?.kind === 'error' ? 'oops' : recentChange ? 'wink' : 'wave';
  const runningStep = busy ? [...chat.entries].reverse().find((entry): entry is Extract<Entry, {kind: 'steps'}> => entry.kind === 'steps')
    ?.steps.find((step) => step.status === 'running') : undefined;
  const status = busy ? runningStep?.label ?? 'Thinking…' : pending ? 'Waiting for your OK' : 'Here to help';

  const toggle = useCallback((next?: boolean) => {
    setOpen((current) => {
      const value = next ?? !current;
      remember(`${storageKey}:open`, value);
      return value;
    });
    setMet(true);
    remember(`${storageKey}:met`, true);
  }, [storageKey]);

  useEffect(() => {
    if (open) window.setTimeout(() => input.current?.focus(), 60);
    // Docked, the page makes room instead of hiding under the panel.
    const dock = window.matchMedia(DOCK_QUERY);
    const apply = () => document.documentElement.classList.toggle('ebc-asst-docked', open && dock.matches);
    apply();
    dock.addEventListener('change', apply);
    return () => {
      dock.removeEventListener('change', apply);
      document.documentElement.classList.remove('ebc-asst-docked');
    };
  }, [open]);

  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
        event.preventDefault();
        toggle();
      } else if (event.key === 'Escape' && open && panel.current?.contains(document.activeElement)) {
        toggle(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, toggle]);

  useEffect(() => { save(storageKey, chat); }, [chat, storageKey]);
  useEffect(() => { scroller.current?.scrollTo({top: scroller.current.scrollHeight, behavior: 'smooth'}); }, [chat.entries, busy, open]);

  /** Asks and shows the answer as it arrives; true once the answer finished. */
  const run = useCallback(async (request: AssistantRequest, first: Entry[]): Promise<boolean> => {
    setBusy(true);
    setLastRequest({request, entries: first});
    let entries = first;
    let changed = false;
    let finished = false;
    const update = (next: Entry[]) => { entries = next; setChat((current) => ({...current, entries: next})); };
    const push = (entry: Entry) => update([...entries, entry]);
    update(entries);
    try {
      const response = await fetch(endpoint, {method: 'POST', headers: {'content-type': 'application/json'}, body: JSON.stringify(request)});
      if (!response.ok || !response.body || !response.headers.get('content-type')?.includes('ndjson')) {
        const data = await response.json().catch(() => null) as {error?: string} | null;
        push({kind: 'error', text: describeError(data?.error ?? '')});
        return false;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      const handle = (event: AssistantEvent) => {
        if (event.type === 'text') {
          const tail = entries.at(-1);
          if (tail?.kind === 'assistant') update([...entries.slice(0, -1), {kind: 'assistant', text: tail.text + event.text}]);
          else push({kind: 'assistant', text: event.text});
        } else if (event.type === 'tool') {
          const step: Step = {id: event.id, label: event.label, status: event.status, ...(event.link ? {link: event.link} : {})};
          // Only this answer's steps: an earlier answer may have used the same id.
          const at = entries.findIndex((entry, index) => index >= first.length && entry.kind === 'steps' && entry.steps.some((item) => item.id === event.id));
          const tail = entries.at(-1);
          if (at >= 0) {
            update(entries.map((entry, index) => (index === at && entry.kind === 'steps'
              ? {kind: 'steps', steps: entry.steps.map((item) => (item.id === event.id ? step : item))} : entry)));
          } else if (tail?.kind === 'steps') {
            update([...entries.slice(0, -1), {kind: 'steps', steps: [...tail.steps, step]}]);
          } else {
            push({kind: 'steps', steps: [step]});
          }
          if (event.status === 'done' && request.decisions?.[event.id] === 'approve') changed = true;
        } else if (event.type === 'confirm') {
          push({kind: 'confirm', calls: event.calls, state: 'pending'});
        } else if (event.type === 'notice') {
          push({kind: 'notice', text: event.text});
        } else if (event.type === 'error') {
          push({kind: 'error', text: describeError(event.error)});
        } else if (event.type === 'done') {
          finished = true;
          setChat({entries, messages: event.messages});
        }
      };
      for (;;) {
        const {value, done} = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, {stream: true});
        let newline;
        while ((newline = buffer.indexOf('\n')) >= 0) {
          const line = buffer.slice(0, newline).trim();
          buffer = buffer.slice(newline + 1);
          if (line) handle(JSON.parse(line) as AssistantEvent);
        }
      }
    } catch {
      push({kind: 'error', text: 'The connection dropped. Try again.'});
    } finally {
      setBusy(false);
      if (changed) onChanged?.();
    }
    return finished;
  }, [endpoint, onChanged]);

  function ask(text: string) {
    const question = text.trim();
    if (!question || busy || pending) return;
    setDraft('');
    const messages = [...chat.messages, {role: 'user', content: question}];
    setChat((current) => ({...current, messages}));
    void run({messages, page}, [...chat.entries, {kind: 'user', text: question}]);
  }

  function decide(index: number, decisions: Record<string, 'approve' | 'decline'>) {
    const values = Object.values(decisions);
    const state = values.every((value) => value === 'approve') ? 'approved' : values.every((value) => value === 'decline') ? 'declined' : 'mixed';
    const entries = chat.entries.map((entry, at) => (at === index && entry.kind === 'confirm' ? {...entry, state} as Entry : entry));
    // An answer that never arrived leaves the change waiting, so it can be decided again.
    void run({messages: chat.messages, decisions, page}, entries).then((finished) => {
      if (finished) return;
      setChat((current) => ({...current, entries: current.entries.map((entry, at) =>
        (at === index && entry.kind === 'confirm' ? {...entry, state: 'pending'} : entry))}));
    });
  }

  function retry() {
    if (!lastRequest || busy) return;
    void run(lastRequest.request, lastRequest.entries);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    ask(draft);
  }

  function onKey(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      ask(draft);
    }
  }

  function restart() {
    if (busy) return;
    setChat({entries: [], messages: []});
    setLastRequest(null);
    input.current?.focus();
  }

  if (!open) {
    return (
      <button type="button" className={`ebc-asst-launcher${aboveTabBar ? ' tabbed' : ''}${greeting ? ' fresh' : ''}`} onClick={() => toggle(true)}
        aria-label="Open your Earn assistant (Ctrl J)">
        <span className="ebc-asst-launcher-label">{pending ? 'A change is waiting for you' : met ? 'Ask me anything' : `Hi${firstName ? ` ${firstName}` : ''}, I can help`}</span>
        <span className="ebc-asst-launcher-figure">
          <Mascot mood={pending ? 'wait' : 'wave'} size={64} />
          {pending ? <i className="ebc-asst-launcher-dot" /> : null}
        </span>
      </button>
    );
  }

  const lastConfirm = chat.entries.map((entry) => entry.kind).lastIndexOf('confirm');
  const lastSteps = chat.entries.map((entry) => entry.kind).lastIndexOf('steps');
  return (
    <aside className="ebc-asst" aria-label="Earn assistant" ref={panel}>
      <header className="ebc-asst-head">
        <span className="ebc-asst-avatar"><Mascot mood={mood} size={44} className={busy ? 'bob' : ''} /></span>
        <span className="ebc-asst-title"><b>Earn assistant</b><small aria-live="polite">{status}</small></span>
        <button type="button" onClick={restart} disabled={busy || !chat.entries.length} title="New chat" aria-label="New chat"><Fresh /></button>
        <button type="button" onClick={() => toggle(false)} title="Close (Esc)" aria-label="Close"><Close /></button>
      </header>
      <div className="ebc-asst-body" ref={scroller}>
        {chat.entries.length === 0 ? (
          <div className="ebc-asst-welcome">
            <span className="ebc-asst-welcome-figure"><Mascot mood="wave" size={112} /></span>
            <b>{firstName ? `Hi ${firstName}!` : 'Hi there!'}</b>
            <span>I can look things up and get things ready. Nothing changes until you say so.</span>
            <div className="ebc-asst-suggestions">{suggestions.map((item) => (
              <button type="button" key={item} onClick={() => ask(item)}><span>{item}</span><Arrow /></button>
            ))}</div>
          </div>
        ) : chat.entries.map((entry, index) => {
          if (entry.kind === 'user') return <div className="ebc-asst-user" key={index}>{entry.text}</div>;
          if (entry.kind === 'assistant') {
            // The mascot shows beside the first words since the person last spoke or decided.
            const since = chat.entries.slice(0, index).reverse();
            const turnStart = since.findIndex((item) => item.kind === 'user' || item.kind === 'confirm');
            const opensTurn = !since.slice(0, turnStart < 0 ? since.length : turnStart).some((item) => item.kind === 'assistant');
            return (
              <div className="ebc-asst-reply" key={index}>
                {opensTurn ? <Mascot mood="wave" size={28} className="ebc-asst-reply-face" /> : <span className="ebc-asst-reply-face" />}
                <div className="ebc-asst-answer"><Rich text={entry.text} /></div>
              </div>
            );
          }
          if (entry.kind === 'steps') return <Steps steps={entry.steps} live={busy && index === lastSteps} key={index} />;
          if (entry.kind === 'confirm') return <ChangeCard entry={entry} key={index} live={!busy && index === lastConfirm} onDecide={(decisions) => decide(index, decisions)} />;
          if (entry.kind === 'notice') return <div className="ebc-asst-notice" key={index}>{entry.text}</div>;
          return (
            <div className="ebc-asst-error" role="alert" key={index}>
              <Mascot mood="oops" size={40} />
              <span>{entry.text}</span>
              {index === chat.entries.length - 1 && lastRequest && !busy ? <button type="button" onClick={retry}>Try again</button> : null}
            </div>
          );
        })}
        {busy && last?.kind !== 'steps' ? (
          <div className="ebc-asst-thinking" aria-label="Thinking"><Mascot mood="wait" size={28} className="bob" /><span><i /><i /><i /></span></div>
        ) : null}
      </div>
      {pending ? (
        <div className="ebc-asst-wait-ok">Approve the change above, or choose “Not now”, and we’ll carry on.</div>
      ) : (
        <form className="ebc-asst-compose" onSubmit={submit}>
          <textarea ref={input} rows={1} value={draft} onChange={(event) => setDraft(event.target.value)} onKeyDown={onKey}
            placeholder="Ask, or tell me what to do…" maxLength={4000} aria-label="Message" />
          <button type="submit" disabled={busy || !draft.trim()} aria-label="Send"><Send /></button>
        </form>
      )}
    </aside>
  );
}
